import { useContext } from "react";
import { Box } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { Loading } from "@analyticsMD/quil";
import MalnutritionAPIContext from "../../contexts/MalnutritionAPIContext";
import EmbededAppContainer from "../embedded-container";
import ErrorPage from "../error-page";
import MalnutritionSections from "./malnutrition-sections";
import theme from "../../theme";
import { Sizes } from "../../types/loadingSizes";

import type { FC, ReactNode } from "react";

interface MalnutritionCardContentProps {
  onClose?: () => void;
  onExpand?: () => void;
  closeButtonCmpt?: ReactNode;
}

const MalnutritionCardContent: FC<MalnutritionCardContentProps> = ({
  onClose,
  onExpand,
  closeButtonCmpt,
}) => {
  const { riskAssessmentQuery, weightsQuery } = useContext(
    MalnutritionAPIContext,
  );

  const queries = [riskAssessmentQuery, weightsQuery];
  // Disabled queries (no emr params) sit in `idle`, never success/error — treat
  // them as settled so the empty state renders instead of a perpetual spinner.
  const settled = queries.every(
    (q) => q?.isSuccess || q?.isError || q?.isIdle,
  );
  const anyErrors = queries.some((q) => q?.isError);

  // Both endpoints return 200 with a null body when there's nothing for the
  // patient, and the two are independent — so treat each as optional rather
  // than assuming a successful query has data.
  const assessment = riskAssessmentQuery?.data ?? null;
  const weights = weightsQuery?.data ?? null;
  const hasContent = assessment !== null || weights !== null;

  return (
    <ThemeProvider theme={theme}>
      <EmbededAppContainer>
        <Box sx={{ minWidth: "20rem" }}>
          {!settled && !anyErrors && (
            <Box sx={{ marginTop: "25%" }} data-testid="malnutrition-loading">
              <Loading message="Loading..." logoSize={Sizes.Medium} />
            </Box>
          )}
          {anyErrors && (
            <ErrorPage
              error={
                new Error(
                  queries
                    .filter((q) => q?.isError)
                    .map((q) => String(q?.error))
                    .join(", "),
                )
              }
              closeButtonCmpt={closeButtonCmpt}
            />
          )}
          {settled && !anyErrors && !hasContent && (
            <Box
              data-testid="malnutrition-empty"
              sx={{ padding: "2rem 1.5rem", textAlign: "center", color: "text.secondary" }}
            >
              No malnutrition data for this patient.
            </Box>
          )}
          {settled && !anyErrors && hasContent && (
            <MalnutritionSections
              assessment={assessment}
              weights={weights}
              onClose={onClose}
              onExpand={onExpand}
              closeButtonCmpt={closeButtonCmpt}
            />
          )}
        </Box>
      </EmbededAppContainer>
    </ThemeProvider>
  );
};

export default MalnutritionCardContent;
